import type { LucideIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { toneSolidClasses, type Tone } from "@/lib/tones";

interface StatusBadgeProps {
  tone: Tone;
  children: React.ReactNode;
  icon?: LucideIcon;
  /** Ponto colorido em vez de ícone. */
  dot?: boolean;
  title?: string;
  className?: string;
}

export function StatusBadge({ tone, children, icon: Icon, dot, title, className }: StatusBadgeProps) {
  return (
    <Badge tone={tone} title={title} className={cn("whitespace-nowrap", className)}>
      {Icon && <Icon className="h-3 w-3" />}
      {!Icon && dot && <span className={cn("h-1.5 w-1.5 rounded-full", toneSolidClasses[tone])} />}
      {children}
    </Badge>
  );
}

export interface StatusDefinition {
  label: string;
  tone: Tone;
  icon?: LucideIcon;
  /** Rótulo curto para cards apertados. */
  short?: string;
  description?: string;
}

/**
 * Cria o badge de um domínio a partir do mapa valor → {rótulo, tom}. O mapa é
 * a fonte única: listas, kanban, timeline e gráficos leem dele.
 *
 *   export const NEGOCIACAO_STATUS = { negociando: { label: "Negociando", tone: "accent" }, ... }
 *   export const NegociacaoBadge = createStatusBadge(NEGOCIACAO_STATUS, "Negociação");
 */
export function createStatusBadge<Value extends string>(
  definitions: Record<Value, StatusDefinition>,
  displayName: string,
) {
  function DomainStatusBadge({
    value,
    short,
    className,
  }: {
    value: Value | null | undefined;
    short?: boolean;
    className?: string;
  }) {
    if (!value) return null;
    const definition = definitions[value];
    if (!definition) return <StatusBadge tone="neutral" className={className}>{value}</StatusBadge>;
    return (
      <StatusBadge tone={definition.tone} icon={definition.icon} title={definition.description} className={className}>
        {short && definition.short ? definition.short : definition.label}
      </StatusBadge>
    );
  }
  DomainStatusBadge.displayName = `${displayName}Badge`;
  return DomainStatusBadge;
}
